import { convertCartToItems } from '@/util/gtm-helpers'
import { EventBus, Events } from '@/lib/events'
import Cookies from 'js-cookie'
import { useLocalStorage } from '~/lib/helpers/storage'
import { LTStorageKeys } from '~/lib/events/tracker'

let stripe

export const discountAppliesToPackage = (cart, id) => {
  const packages = cart.coupon.applies_to || []

  return !id || !packages.length || packages.includes(id)
}

/**
 * This is the very start of the checkout process.  The call to the /checkout endpoint can result in 2 different
 * branches of code:
 * 1. The checkout session requires payment and the user is sent to Stripe
 * 2. The checkout session is completed, and the user is sent straight to /thank-you
 *
 * The first case is the default case, and happens for every single order that isn't completely paid for with Credits.  This includes 100%
 * off orders as a result of Stripe Coupons, such as when a coupon reduces a subscriptions first month to $0.
 *
 * The second case will only ever happen when a user is using a Credit, AND, every item in the cart has a credit assigned.  If the user adds an addon,
 * or has 2 logos in their cart but only 1 credit, they will still be required to go through Checkout.
 */
export const beginCheckoutFlow = async ({
  $api,
  $getAuthInstance,
  store,
  router,
  cancel_page,
  custom_checkout = false,
}) => {
  const tap_ref = Cookies.get('ref')
  const tap_vid = Cookies.get('tap_vid')
  const sscid = Cookies.get('sscid')
  const referrer = Cookies.get('logoReferrer')
  const user_agent = window.navigator.userAgent.toLowerCase()
  
  const localStorage = useLocalStorage()

  const session_id = localStorage.getItem(LTStorageKeys.SESSION_ID)
  const anonymous_id = localStorage.getItem(LTStorageKeys.ANONYMOUS_ID)
  const referring_product = localStorage.getItem(
    LTStorageKeys.REFERRING_PRODUCT
  )
  const country_code = localStorage.getItem(LTStorageKeys.COUNTRY_CODE)
  const adblocker_detected = !window.ga || (!!window.ga && !window.ga.loaded)

  const usedFeatureFlags = store.state.flags.updated_flags.reduce(
    (acc, flag) => {
      acc[flag] = store.state.flags[flag]

      return acc
    },
    {}
  )


  const checkoutParams = {
    cart: store.state.cart.id,
    cancel_page,
    custom_checkout,
    enable_tax: true,
    metadata: {
      flags: usedFeatureFlags,
      tap_ref,
      tap_vid,
      referrer,
      sscid,
      user_agent,
      session_id,
      anonymous_id,
      referring_product,
      country_code,
      adblocker_detected,
    },
  }

  const auth = await $getAuthInstance()
  const user = await auth.currentAuthenticatedUser().catch(() => null)
  if (!user) {
    throw new Error('An account is required to purchase a logo.')
  }

  const { data: order } = await $api.post('/api/v2/orders', checkoutParams)

  // Tee up analytics
  EventBus.$emit(Events.EcommerceCheckout, convertCartToItems(store.state.cart))

  if (custom_checkout) {
    store.commit('cart/setOrder', order)

    return order
  }

  if (order.checkout_session) {
    store.commit('cart/setOrder', order)

    if (!stripe) {
      stripe = Stripe(process.env.STRIPE_PUBLIC_KEY)
    }

    // The customer is required to pay, so let's send them over to Stripe
    const { error } = await stripe.redirectToCheckout({
      sessionId: order.checkout_session,
    })

    if (error) {
      throw error
    }
  } else {
    store.commit('cart/setOrder', order)

    // TODO: remove AppsumoCheckout references
    Cookies.remove('AppsumoCheckout')
    Cookies.remove('credit_checkout')

    // The order was free.  The resulting object is an Order
    router.replace({
      path: '/thank-you',
      query: { order: order.id },
    })
  }

  return order
}
